import { hash32, seedFromString } from '@gardener/core';
import { ticks } from './config';
import { isRude } from './moderation';

/**
 * Online play rules shared by the server (validation) and the client (the same checks while typing, ADR-0015).
 * Names and room codes are drawn in the pixel font, so they only use characters it has.
 */
export const PLAYER_NAME = {
  minLength: 2,
  maxLength: 14,
  /** What a name may contain (after `normalizeName`): letters, digits, single spaces, dots and dashes. */
  pattern: /^[A-Z0-9][A-Z0-9 .-]*[A-Z0-9.]$/,
} as const;

/** Uppercase (the font has no lowercase), drop characters the font can't draw, collapse spaces. */
export function normalizeName(raw: string): string {
  return raw
    .toUpperCase()
    .replace(/[^A-Z0-9 .-]/g, '')
    .replace(/\s+/g, ' ')
    .trimStart()
    .slice(0, PLAYER_NAME.maxLength);
}

/** Why `name` (already normalized) can't be used, or null. Short, uppercase, for the UI. */
export function nameProblem(name: string): string | null {
  const n = name.trim();
  if (n.length < PLAYER_NAME.minLength) return `AT LEAST ${PLAYER_NAME.minLength} CHARACTERS`;
  if (n.length > PLAYER_NAME.maxLength) return `AT MOST ${PLAYER_NAME.maxLength} CHARACTERS`;
  if (!PLAYER_NAME.pattern.test(n)) return 'LETTERS, DIGITS, SPACES, . AND - ONLY';
  // Bots are `<NAME> BOT` (a second one `<NAME> BOT 2`): a player can't pass for one.
  if (/ BOT( \d+)?$/.test(n)) return 'NAMES ENDING IN BOT ARE FOR BOTS';
  // The word filter (ADR-0156).
  if (isRude(n)) return 'PICK ANOTHER NAME';
  return null;
}

/**
 * Streamer mode's stand-in for a player's name (ADR-0128): `GARDENER 417`, picked by their account id (a guest's: any
 * stable id), the same on every screen and every client, so a stream tells players apart without showing who they are.
 * Always passes `nameProblem`.
 */
export const standIn = (id: string): string => `GARDENER ${100 + (hash32(seedFromString(id), 0x5717) % 900)}`;

/** What players are called, word by word: `guiseNames` builds bots' names that pass for theirs out of these. */
const PEOPLE = `
  KUBA OLEK MAREK TOMEK KAMIL PIOTR ADAM KACPER FILIP WIKTOR ANIA KASIA ZUZIA NINA LENA MAJA EMIL IGOR OSKAR MILO
  LUKAS JONAS FELIX LEON NOAH EMMA LUCA MATEO HUGO TOM MAX BEN SAM ALEX JAKE RYAN LIAM OWEN ELLIE CHLOE MILA TEO
  NICO JULES PABLO SVEN ERIK TIMO JAN DANI`
  .trim()
  .split(/\s+/);
const THINGS = `
  PICKLE TURNIP RADISH POTATO CARROT CACTUS MUSHROOM TOAD FROG GOBLIN WIZARD NINJA PIRATE VIKING SHADOW GHOST
  RAVEN WOLF FOX PANDA KOALA OTTER BADGER BEAR TIGER DRAGON ROCKET BLAZE STORM NOVA PIXEL GLITCH NOODLE WAFFLE
  TACO MANGO PEACH BEAN SNAIL BEETLE MOLE GECKO LLAMA DUCK GOOSE HAMSTER COMET VIPER SPUD SNIPER`
  .trim()
  .split(/\s+/);
const KINDS = `
  SPICY TINY BIG SNEAKY SALTY LUCKY CRISPY FUZZY MOSSY ANGRY SLEEPY SILENT CRAZY DARK GOLDEN ROGUE MIGHTY SWIFT
  LAZY FERAL`
  .trim()
  .split(/\s+/);
const TITLES = 'MR SIR LORD DR KING BABY'.split(' ');

/**
 * Names for the bots a search that found nobody plays (ADR-0147): `n` names as players pick them (`KUBA07`, `SPICY
 * TOAD`, `NINJAFOX`…), drawn by `seed`, none of `taken` (who else plays in the room) and no two alike. Each passes
 * `nameProblem`, so none ends in BOT.
 */
export function guiseNames(seed: number, n: number, taken: readonly string[] = []): string[] {
  const out: string[] = [];
  for (let i = 0; out.length < n; i++) {
    const h = (k: number) => hash32(seed, i, k);
    const of = (list: readonly string[], k: number) => list[h(k) % list.length] as string;
    const [who, what, kind, more] = [of(PEOPLE, 1), of(THINGS, 2), of(KINDS, 3), of(THINGS, 4)];
    const digits = String(h(5) % 100).padStart(h(6) % 2 ? 2 : 1, '0');
    const shapes = [
      who,
      `${who}${digits}`,
      `${kind} ${what}`,
      `${kind}${what}`,
      `${what}${digits}`,
      `${of(TITLES, 7)} ${what}`,
      `${what}${more}`,
      `${who} ${what}`,
    ];
    const name = shapes[h(0) % shapes.length] as string;
    if (!nameProblem(name) && !taken.includes(name) && !out.includes(name)) out.push(name);
  }
  return out;
}

/**
 * Chat is for 13 and up (ADR-0229; ADR-0122 asked everyone before they played). A player is asked how old they are
 * the first time they open a chat, once: a tap on their age from `choices`, every one offered and none picked for them
 * (a neutral age screen), its first meaning that age or under and its last that age or over. Only its bracket is kept
 * (`ageBracketOf`), never the age.
 */
export const AGE = { min: 13, choices: [8, 9, 10, 11, 12, 13, 14, 15, 16, 17, 18, 19] } as const;
export const AGE_BRACKETS = ['under-13', '13-15', '16-17', '18+'] as const;
export type AgeBracket = (typeof AGE_BRACKETS)[number];
export const isAgeBracket = (s: string): s is AgeBracket => (AGE_BRACKETS as readonly string[]).includes(s);

/** The bracket of someone `years` old. */
export const ageBracketOf = (years: number): AgeBracket =>
  years < AGE.min ? 'under-13' : years < 16 ? '13-15' : years < 18 ? '16-17' : '18+';

/**
 * May an account with bracket `age` chat (ADR-0229): the room's chat, friends' chat? Only once it told an age of 13 or
 * more; none told (never asked yet, an account older than the question) is no chat until it is.
 */
export const canChat = (age: AgeBracket | null | undefined): boolean => !!age && age !== 'under-13';

/**
 * The words of the name a first-time player plays as (ADR-0229: rolled for them as the game opens), "SNEAKY TULIP": a
 * lively word, then a garden one. At most 6 + 1 + 7 letters, so every pair passes `nameProblem` (a test says so).
 */
export const GARDEN_NAME = {
  first: [
    'SNEAKY',
    'SUNNY',
    'MOSSY',
    'SPIKY',
    'LEAFY',
    'ZESTY',
    'JOLLY',
    'BRAVE',
    'SWIFT',
    'WILD',
    'PLUCKY',
    'FUZZY',
    'CRISPY',
    'LUCKY',
    'ROWDY',
    'PEPPY',
    'THORNY',
    'DEWY',
    'NIMBLE',
    'SNAPPY',
    'CHEEKY',
    'FROSTY',
    'GRUMPY',
    'MIGHTY',
  ],
  second: [
    'TULIP',
    'TURNIP',
    'RADISH',
    'CARROT',
    'PUMPKIN',
    'PEPPER',
    'CACTUS',
    'THISTLE',
    'CLOVER',
    'BRAMBLE',
    'ACORN',
    'BEETLE',
    'SPROUT',
    'ONION',
    'POTATO',
    'CABBAGE',
    'DAISY',
    'POPPY',
    'FERN',
    'TOMATO',
    'NETTLE',
    'BASIL',
    'GARLIC',
    'MELON',
    'SNAIL',
    'GNOME',
    'PARSNIP',
    'CHIVE',
  ],
} as const;

/** A name for a first-time player; `roll` in [0, 1) picks it (the caller's randomness: content is pure). */
export function gardenName(roll: number): string {
  const { first, second } = GARDEN_NAME;
  const i = Math.floor(Math.min(Math.max(roll, 0), 0.999_999) * first.length * second.length);
  return `${first[i % first.length]} ${second[Math.floor(i / first.length)]}`;
}

export const ROOM = {
  /** Room codes: this many characters from `alphabet` (no 0/O and 1/I/L, so nobody mistypes one). */
  codeLength: 5,
  alphabet: 'ABCDEFGHJKMNPQRSTUVWXYZ23456789',
  /** A room nobody is in closes after this long (ms). */
  idleCloseMs: 5 * 60_000,
  /** …a battle royale's sooner. */
  royaleIdleCloseMs: 30_000,
  /**
   * A stream room (ADR-0132): its owner's START counts this down, time for the viewers it draws from its bench to come
   * in (a creator's Battle Royale room counts `ROYALE.room.waitTicks`, as long).
   */
  streamStartTicks: ticks(8),
} as const;

/**
 * What a games room plays (ADR-0174, the menus' Custom game): its owner picks one of them on its stage and its START
 * plays it with everyone in the room, on the server; a Zombie Nights run's or a battle royale's end brings them back to
 * the stage. A games room seats `GAMES_ROOM`.
 */
export const ROOM_GAMES = ['zombies', 'royale', 'range'] as const;
export type RoomGame = (typeof ROOM_GAMES)[number];
/** The players a games room seats (ADR-0174): Zombie Nights' most, a party's (`PARTY.maxSize`). */
export const GAMES_ROOM = 5;

/**
 * Line-ups (ADR-0045): players a team, `'1v1'` to `'5v5'`. Nothing queues for one since Classic is gone (ADR-0209): a
 * room's line-up says how many it seats a team, and a battle royale's rounds store their format as one (`'1v1'` solo up
 * to `'5v5'` squads of five).
 */
export const QUEUES = {
  '1v1': { teamSize: 1 },
  '2v2': { teamSize: 2 },
  '3v3': { teamSize: 3 },
  '4v4': { teamSize: 4 },
  '5v5': { teamSize: 5 },
} as const;

export type QueueMode = keyof typeof QUEUES;

export const QUEUE_MODES = Object.keys(QUEUES) as QueueMode[];

export const isQueueMode = (key: string): key is QueueMode => Object.hasOwn(QUEUES, key);

/** The line-up with `n` players a team (1-5). */
export const lineUpOf = (n: number): QueueMode =>
  `${Math.min(5, Math.max(1, Math.round(n)))}v${Math.min(5, Math.max(1, Math.round(n)))}` as QueueMode;

/**
 * A party (the party ADR): you and up to `maxSize - 1` friends, always there (alone, a party of one). Anyone in it
 * invites, a friend of a member walks in; the leader starts what the party plays.
 */
export const PARTY = {
  maxSize: 5,
} as const;

/**
 * Dropping out of a room (ADR-0045): the Match holds a signed-in player's seat (gardener, team, gold, gun, stats) this
 * long (2 minutes since feedback #32, was 60 s), and a new connection of the same account takes it back. The client
 * retries its room for as long, waiting `retryMs` between attempts (the last one repeats: every second, as a link that
 * stalls for a few seconds is back in one), each attempt given up after `connectMs` without the server's welcome (a
 * handshake a stalled link holds up); a reload shows it in the menus for as long, to rejoin or leave.
 */
export const REJOIN = {
  holdTicks: ticks(120),
  retryMs: [500, 1000],
  connectMs: 4000,
} as const;

/** Uppercase, alphabet-only, at most `codeLength` characters (for the code field). */
export function normalizeRoomCode(raw: string): string {
  const allowed = new Set(ROOM.alphabet);
  return [...raw.toUpperCase()]
    .filter((ch) => allowed.has(ch))
    .join('')
    .slice(0, ROOM.codeLength);
}

export const isRoomCode = (code: string): boolean =>
  code.length === ROOM.codeLength && [...code].every((ch) => ROOM.alphabet.includes(ch));

/**
 * Friends (ADR-0055): everyone has a friend code, `codeLength` characters from the room-code alphabet (~40 bits), shown
 * as `XXXX-XXXX`. Whoever uses your code becomes your friend at once, both ways: sharing it is the consent. Friends see
 * each other online and can invite each other; nobody else sees either. At most `max` friends each.
 */
export const FRIENDS = { max: 100, codeLength: 8 } as const;

/** What a typed or pasted code is compared by: uppercase, only the alphabet's characters (dashes, spaces dropped). */
export const normalizeFriendCode = (raw: string): string =>
  [...raw.toUpperCase()].filter((ch) => ROOM.alphabet.includes(ch)).join('');

/** How a friend code reads: `ABCD-EFGH`. */
export const formatFriendCode = (code: string): string => `${code.slice(0, 4)}-${code.slice(4)}`;

/**
 * Signing in with a login and a password (ADR-0055), on top of the token a browser keeps. The login is unique whatever
 * its case (kept lowercase) and separate from the name others see, which stays free and renamable.
 */
export const LOGIN = { minLength: 3, maxLength: 20, pattern: /^[a-z0-9_]+$/ } as const;
export const PASSWORD = { minLength: 8, maxLength: 128 } as const;

/** A login as it is kept and compared: lowercase, no spaces around it. */
export const normalizeLogin = (raw: string): string => raw.trim().toLowerCase();

/** Why `login` (already normalized) can't be used, or null. Short, uppercase, for the UI. */
export function loginProblem(login: string): string | null {
  if (login.length < LOGIN.minLength || login.length > LOGIN.maxLength)
    return `A LOGIN IS ${LOGIN.minLength} TO ${LOGIN.maxLength} CHARACTERS`;
  if (!LOGIN.pattern.test(login)) return 'LETTERS, DIGITS AND _ ONLY';
  return null;
}

/** Why `password` can't be used, or null. */
export function passwordProblem(password: string): string | null {
  if (password.length < PASSWORD.minLength) return `A PASSWORD IS AT LEAST ${PASSWORD.minLength} CHARACTERS`;
  if (password.length > PASSWORD.maxLength) return `A PASSWORD IS AT MOST ${PASSWORD.maxLength} CHARACTERS`;
  return null;
}
